import { CustomTextInput } from "../../../../components/CustomTextInput/CustomTextInput";
import { useTranslation } from "react-i18next";
import { CustomTextDatePicker } from "../../../../components/CustomTextDatePicker/CustomTextDatePicker";
import { CustomTypography } from "../../../../components/CustomTypography/CustomTypography";
import { CustomButton } from "../../../../components/CustomButton/CustomButton";
import useStyles from "./styles";
import { colors } from "../../../../styles/colors";
import { ChangeEvent, FC, ReactNode, useMemo, useRef, useState } from "react";
import { CustomTextSelect } from "../../../../components/CustomTextSelect/CustomTextSelect";
import { ModalLeasing } from "./ModalLeasing/ModalLeasing";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  SelectChangeEvent,
} from "@mui/material";
import { useFormik } from "formik";

import { useGetAllBrands, useGetAllModel } from "../../../../api/brandModel";
import { getModelPictureUrl } from "../../../../api/media";
import { CustomSearch } from "../../../../components/CustomSearch/CustomSearch";
import { getVehicleStatusOptions } from "../../../../utils/types";
import dayjs from "dayjs";
import {
  validationSchemaCreateVehicle,
  initialValuesVehicle,
} from "./validationShema";
import { useCreateVehicule } from "../../../../api/vehicule";
import { getCurrentFleetId } from "../../../../utils/getCurrentFleet";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { FieldError } from "../../../../components/FieldError/FieldError";
import {
  fieldError,
  missingFieldsMessage,
  touchAll,
} from "../../vehicleFormErrors";

type Props = {
  onClickSaveAndNext?: () => void;
};

export const StepCreateFileVehicule: FC<Props> = ({ onClickSaveAndNext }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  // See ModalUpdateVehicle: translated label, code value, no round-trip
  // through a French display string.
  const statusOptions = useMemo(() => getVehicleStatusOptions(t), [t]);

  const dispatch = useDispatch();

  const [openLeasing, setOpenLeasing] = useState(false);
  const [brand, setbrand] = useState<TBrand | null>(null);

  const { brands } = useGetAllBrands();
  const { brandsModel } = useGetAllModel(brand?.id ?? null);

  const { mutate: createVehicle, isPending } = useCreateVehicule(
    getCurrentFleetId(),
    onClickSaveAndNext
  );

  // Synchronous guard against a double click: isPending only flips on the
  // next render, so two clicks in the same tick used to send two POSTs.
  // (The second was rejected server-side by the VIN/number uniqueness check,
  // but surfaced as a confusing "VIN already exists" right after success.)
  const submittingRef = useRef(false);

  const [selectedRental, setSelectedRental] = useState({
    label: "",
    value: "",
  });

  const formik = useFormik({
    initialValues: initialValuesVehicle,
    validationSchema: validationSchemaCreateVehicle,
    onSubmit: (values) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      createVehicle(
        {
        // required api
        vin: values.vin ?? "",
        number: values.immatriculation ?? "",
        owner_company_id:
          selectedRental.label.trim().length === 0
            ? getCurrentFleetId()
            : Number(selectedRental.value),
        brand_model_id: Number(values.modele) ?? 0,
        vehicle_status: values.status ?? "F",
        monthly_average_cost: 0,
        // not required api
        boite: values.gearBox ?? "",
        puissance: values.fiscalPower ?? "",
        carburant: values.energy ?? "",
        mise_en_circulation: values.releaseDate ?? "",
        picture: getModelPictureUrl(Number(values.modele)),
        register_step: 0,
        },
        {
          onSettled: () => {
            submittingRef.current = false;
          },
        }
      );
    },
  });

  const errorOf = (field: keyof typeof formik.values) =>
    fieldError(t, formik.errors, formik.touched, field);

  // Options currently selected, derived from the ids held by Formik. The
  // fields are strict (CustomSearch strictOptions), so they must be
  // controlled: an uncontrolled strict Autocomplete cannot be reset when the
  // brand changes.
  const selectedBrand =
    (brands ?? []).find((b: any) => String(b?.id) === formik.values.brand) ??
    null;
  const selectedModel =
    (brandsModel ?? []).find(
      (m: any) => String(m?.id) === formik.values.modele
    ) ?? null;

  const onChangeSelectRentalCompany = (
    event: SelectChangeEvent<any>,
    _: ReactNode
  ) => {
    setSelectedRental(event.target.value as { label: string; value: string });
    dispatch({ type: "OWNER_COMPANY", data: event.target.value });
  };

  const onChangeAutoCompletBrand = (_: any, brand: any) => {
    // Only a real option (an object with an id) is a brand. Anything else -
    // null on a cleared field, or a bare string - means "no brand chosen";
    // `String(brand.id)` used to turn a typed string into "undefined", which
    // PASSED validation and then left the model list empty forever.
    const picked = brand && typeof brand === "object" && brand.id ? brand : null;
    const changed = String(picked?.id ?? "") !== formik.values.brand;
    setbrand(picked as TBrand | null);
    formik?.setFieldValue("brand", picked ? String(picked.id) : "");
    // A model belongs to ONE brand: keep it only while the brand is unchanged.
    if (changed) formik?.setFieldValue("modele", "");
  };

  const onChangeAutoCompletModele = (_: any, modele: any) => {
    const picked = modele && typeof modele === "object" && modele.id ? modele : null;
    formik?.setFieldValue("modele", picked ? String(picked.id) : "");
  };

  const handleRadioChange = (
    _: ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    formik.setFieldValue("isLeasing", value);
    if (value === "yes") {
      setOpenLeasing(true);
    } else {
      setSelectedRental({ label: "", value: "" });
      dispatch({ type: "OWNER_COMPANY", data: { label: "", value: "" } });
      setOpenLeasing(false);
    }
  };

  const onCloseLeasing = () => {
    setOpenLeasing(false);
    // FIX: Use "" instead of null â€” Yup.string() rejects null in v1
    formik?.setFieldValue("isLeasing", "");
    setSelectedRental({ label: "", value: "" });
    dispatch({ type: "OWNER_COMPANY", data: { label: "", value: "" } });
  };

  const onSaveLeasing = () => {
    if (selectedRental?.label?.trim()?.length > 0) {
      setOpenLeasing(false);
    } else {
      toast.error(
        t("required.selectRentalCompany", "Please select a rental company")
      );
    }
  };

  // FIELD ORDER of the summary toast = order on screen.
  const fieldLabels = {
    vin: t("vehicule.vin", "VIN"),
    immatriculation: t("vehicule.registration", "Registration"),
    brand: t("vehicule.brand", "Brand"),
    modele: t("vehicule.model", "Model"),
    gearBox: t("vehicule.gearBox", "Gearbox"),
    fiscalPower: t("vehicule.fiscalePower", "Fiscal power"),
    energy: t("vehicule.fuel", "Fuel"),
    releaseDate: t("vehicule.release", "Release"),
    status: t("vehicule.status", "Status"),
    isLeasing: t("vehicule.rentShort", "Rental / leasing"),
  };

  /**
   * Save is ALWAYS reachable (except while a request is in flight). An
   * invalid form now explains itself - every blocking field is outlined with
   * its message and one toast lists them - instead of the button sitting
   * greyed out with no reason given, which is what the STC fleet manager
   * reported.
   */
  const handleSave = async () => {
    if (isPending || submittingRef.current) return;
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched(touchAll(formik.values), false);
      toast.error(missingFieldsMessage(t, errors, fieldLabels));
      return;
    }
    formik.handleSubmit();
  };

  return (
    <>
      <ModalLeasing
        open={openLeasing}
        title={t("vehicule.societyName", "Adding rental company name")}
        valueSelectedRental={selectedRental}
        onSave={onSaveLeasing}
        onCancel={onCloseLeasing}
        onClose={onCloseLeasing}
        onChange={onChangeSelectRentalCompany}
      />

      <form className={classes.divForm}>
        <div className={classes.container}>
          <div className={classes.containerInput}>
            <CustomTextInput
              name="vin"
              value={formik?.values?.vin}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label={`${t("vehicule.vin", "VIN ")} *`}
              placeholder={t("vehicule.vin", "VIN")}
              helperTextError={errorOf("vin")}
            />
            <CustomTextInput
              name="immatriculation"
              value={formik?.values?.immatriculation}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label={`${t("vehicule.registration", "registration")} *`}
              placeholder={t("vehicule.registration", "Registration")}
              helperTextError={errorOf("immatriculation")}
            />
          </div>
          <div className={classes.containerInput}>
            <CustomSearch
              options={brands}
              getOptionLabel={(option: any) => option?.name}
              label={`${t("vehicule.brand", "Brand")} *`}
              placeholder={t("vehicule.brand", "Brand")}
              strictOptions
              value={selectedBrand}
              onChange={onChangeAutoCompletBrand}
              helperTextError={errorOf("brand")}
              onChangeTextField={(event) => {
                if (event.target.value?.trim().length === 0) {
                  setbrand(null);
                  // FIX: Use "" instead of null
                  formik?.setFieldValue("brand", "");
                  formik?.setFieldValue("modele", "");
                }
              }}
            />
            <CustomSearch
              options={brandsModel}
              getOptionLabel={(option: any) => option?.name}
              label={`${t("vehicule.model", "Model")} *`}
              placeholder={t("vehicule.model", "Model")}
              strictOptions
              value={selectedModel}
              onChange={onChangeAutoCompletModele}
              helperTextError={errorOf("modele")}
              onChangeTextField={(event) => {
                if (event.target.value?.trim().length === 0) {
                  // FIX: Use "" instead of null
                  formik?.setFieldValue("modele", "");
                }
              }}
            />
          </div>
          <div className={classes.containerInput}>
            <CustomTextSelect
              label={`${t("vehicule.gearBox", "Gearbox")} *`}
              options={["Automatique", "Manuelle"]}
              placeholder={t("vehicule.gearBox", "Gearbox")}
              name="gearBox"
              value={formik?.values?.gearBox}
              onChange={formik.handleChange}
              helperTextError={errorOf("gearBox")}
            />
            <CustomTextInput
              label={`${t("vehicule.fiscalePower", "Fiscal power")} *`}
              placeholder={t("vehicule.fiscalePower", "Fiscal power")}
              name="fiscalPower"
              value={formik?.values?.fiscalPower}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              helperTextError={errorOf("fiscalPower")}
            />
          </div>
          <div className={classes.containerInput}>
            <CustomTextSelect
              label={`${t("vehicule.fuel", "Fuel")} *`}
              options={[
                "Essence",
                "Diesel",
                "Electrique",
                "GPL",
                "Hydrogène",
                "Ethanol",
                "Hybride",
                "hybride rechargeable",
              ]}
              placeholder={t("vehicule.fuel", "Fuel")}
              name="energy"
              value={formik?.values?.energy}
              onChange={formik.handleChange}
              helperTextError={errorOf("energy")}
            />
            <CustomTextDatePicker
              label={`${t("vehicule.release", "Release")} *`}
              // Controlled: without `value` the picker was bound to null.
              value={formik?.values?.releaseDate || null}
              helperTextError={errorOf("releaseDate")}
              onChange={(value) =>
                // The picker reports null for an empty OR half-typed date.
                // dayjs(null).format() is the STRING "Invalid Date", which
                // passed `required` and reached MySQL as a DATE.
                formik?.setFieldValue(
                  "releaseDate",
                  value && dayjs(value).isValid()
                    ? dayjs(value).format("YYYY-MM-DD")
                    : ""
                )
              }
            />
          </div>
          <div className={classes.containerInput}>
            <CustomTextSelect
              label={`${t("vehicule.status", "Status")} *`}
              options={statusOptions}
              placeholder={t("vehicule.status", "Status")}
              name="status"
              value={formik?.values?.status}
              onChange={formik.handleChange}
              helperTextError={errorOf("status")}
            />
          </div>
          <div className={classes.divRent}>
            <CustomTypography
              text={t("vehicule.rent", "Is this a rental or leasing car? *")}
              variant={"link"}
            />
            <FormControl>
              <RadioGroup
                row
                value={formik?.values?.isLeasing}
                name="isLeasing"
                onChange={(event) => {
                  handleRadioChange(event, event.target.value);
                }}
              >
                <FormControlLabel
                  value={"no"}
                  control={<Radio />}
                  label={t("common.no", "No")}
                />
                <FormControlLabel
                  value={"yes"}
                  control={<Radio />}
                  label={t("common.yes", "Yes")}
                />
              </RadioGroup>
            </FormControl>
          </div>
          <FieldError message={errorOf("isLeasing")} />
          <div className={classes.containerRequire}>
            <CustomTypography
              variant={"link"}
              color={colors.primary}
              text={t(
                "vehicule.allFieldsAreRequired",
                "All fields are required"
              )}
            />
          </div>
          <div className={classes.containerAllButton}>
            <div className={classes.containerButton}>
              <CustomButton
                label={t("common.saveButton", "Save")}
                variant={"default"}
                disabled={isPending}
                width={"fit-content"}
                height={"40px"}
                onClick={handleSave}
              />
              <CustomButton
                label={t("vehicule.saveAndNext", "Save and next")}
                variant={"primary"}
                loading={isPending}
                width={"fit-content"}
                onClick={handleSave}
                height={"40px"}
              />
            </div>
          </div>
        </div>
      </form>
    </>
  );
};
